import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';

import { ChildGlossaryTermFragment } from '../../graphql/glossaryNode.generated';
import { GlossaryNodeFragment } from '../../graphql/fragments.generated';
import { GlossaryNode, GlossaryTerm } from '../../types.generated';

import { useEntityRegistry } from '../useEntityRegistry';
import GlossaryEntityItem from './GlossaryEntityItem';
import { PaginationBar } from '../entity/shared/tabs/Glossary/PaginationBar';

const EntitiesWrapper = styled.div`
    flex: 1;
    overflow: auto;
    background-color: #ffffff;

    a {
        display: block;
    }
`;

interface Props {
    page: number;
    pageSize: number;
    canExport?: boolean;
    canDeleteTerm?: boolean;
    canDeleteNode?: boolean;
    selectedItems: string[];
    terms: (GlossaryTerm | ChildGlossaryTermFragment)[];
    nodes: (GlossaryNode | GlossaryNodeFragment)[];
    onChangePage: (newPage: number) => void;
    setSelectedItems: (items: string[]) => void;
    onShowSizeChange: (newPageSize: number) => void;
}

function GlossaryEntitiesList(props: Props) {
    const {
        page,
        terms,
        nodes,
        pageSize,
        selectedItems,
        canExport = true,
        canDeleteTerm = true,
        canDeleteNode = true,
        onChangePage,
        setSelectedItems,
        onShowSizeChange,
    } = props;
    const entityRegistry = useEntityRegistry();

    const [paginatedItems, setPaginatedItems] = useState<
        Array<GlossaryNode | GlossaryNodeFragment | GlossaryTerm | ChildGlossaryTermFragment>
    >([]);
    const totalCount = nodes.length + terms.length;

    // Update paginated items when page, pageSize, nodes, or terms change
    useEffect(() => {
        const startIndex = (page - 1) * pageSize;
        // Combine nodes and terms into a single array
        const allItems = [...nodes, ...terms];
        const endIndex = Math.min(startIndex + pageSize, allItems.length);

        // Get paginated items for current page
        const currentPageItems = allItems.slice(startIndex, endIndex);
        setPaginatedItems(currentPageItems);
    }, [page, pageSize, nodes, terms]);

    const handleItemCheck = (event, glossary) => {
        event.preventDefault();
        event.stopPropagation();

        let updatedGlossaryUrns = [...selectedItems];

        if (updatedGlossaryUrns.includes(glossary.urn)) {
            updatedGlossaryUrns = updatedGlossaryUrns.filter((name) => name !== glossary.urn);
        } else {
            updatedGlossaryUrns.push(glossary.urn);
        }

        setSelectedItems(updatedGlossaryUrns);
    };

    return (
        <EntitiesWrapper className="f-entities-wrapper">
            {paginatedItems.map((item) => {
                const isNode = nodes.some((node) => node.urn === item.urn);

                if (isNode) {
                    const node = item as GlossaryNode | GlossaryNodeFragment;
                    return (
                        <GlossaryEntityItem
                            key={node.urn}
                            urn={node.urn}
                            type={node.type}
                            canExport={canExport}
                            canDelete={canDeleteNode}
                            selectedItems={selectedItems}
                            name={node.properties?.name || ''}
                            handleItemCheck={(e) => handleItemCheck(e, node)}
                            count={(node as GlossaryNodeFragment).children?.total}
                        />
                    );
                }
                const term = item as GlossaryTerm | ChildGlossaryTermFragment;
                return (
                    <GlossaryEntityItem
                        key={term.urn}
                        urn={term.urn}
                        type={term.type}
                        canDelete={canDeleteTerm}
                        canExport={canExport}
                        selectedItems={selectedItems}
                        name={entityRegistry.getDisplayName(term.type, term)}
                        handleItemCheck={(e) => handleItemCheck(e, term)}
                    />
                );
            })}

            <PaginationBar
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                onChangePage={onChangePage}
                onShowSizeChange={onShowSizeChange}
            />
        </EntitiesWrapper>
    );
}

export default GlossaryEntitiesList;
